<template>
  <div>
    <Portal to="top-sticky-bar">
      <transition name="fade">
        <div
          v-if="topStickyBar"
          id="top-sticky-bar"
          class="absolute bg-white w-full pt-2 px-3 md:px-6 flex-row pb-2"
        >
          <div
            class="flex flex-row justify-start items-center max-w-1140 w-full mx-auto"
            data-test-id="top-sticky-bar"
          >
            <DismissButton v-if="dismissible" @close="close" />
            <Avatar
              data-test-id="athlete-avatar"
              :show-verified="true"
              :show-fav-interested="false"
              :size="topStickyAvatarSize"
              verified-tooltip-position="right"
            />
            <div
              class="m-0 ml-2 text-left"
            >
              <h1
                class="ds-text-xs md:ds-text-sm font-semibold m-0"
                data-test-id="athlete-basic-info"
              >
                <span data-test-id="athlete-full-name">{{ userFullName }}</span>
                <span v-if="user.classYear" data-test-id="class-year"> • {{ user.classYear }}</span>
                <span v-if="user.isTransfer" data-test-id="transfer-tag"> Transfer</span>
              </h1>
              <h3
                v-if="!isMobile && position"
                class="font-medium text-gray-500 m-0 ds-text-sm"
                data-test-id="md-position-list"
              >
                {{ positionList }}
              </h3>
            </div>
            <div
              class="absolute top-0 bottom-0 ml-1 flex items-center md:static md:ml-auto"
              :style="{
                right: isMobile ? '0.75rem' : '0'
              }"
            >
              <ShareDropdown v-if="!isApplicableCollegeCoach" :athlete-full-name="userFullName" />
              <CoachDropdown v-if="isApplicableCollegeCoach" :athlete-full-name="userFullName" />
            </div>
          </div>
        </div>
      </transition>
    </Portal>
    <Portal to="identifying-bar">
      <div
        id="identifying-bar"
        class="flex items-center justify-center md:justify-between bg-white w-full pt-2 px-3 md:px-6 flex-row pb-3 md:pt-6 md:pb-4 relative"
      >
        <div
          class="flex flex-col md:flex-row items-center max-w-1140 w-full mx-auto"
          data-test-id="regular-bar"
        >
          <div
            class="absolute top-0 flex md:static"
            :style="{
              paddingTop: isMobile ? '1rem' : '0',
              left: isMobile ? '0.75rem' : '0'
            }"
          >
            <DismissButton v-if="dismissible" @close="close" />
          </div>
          <Avatar
            data-test-id="athlete-avatar"
            :show-verified="true"
            :show-fav-interested="false"
            :size="avatarSize"
          />
          <div
            class="m-0 md:ml-2 text-center md:text-left"
          >
            <h1
              class="ds-text-sm md:ds-text-base font-semibold m-0 mt-2 md:mt-0"
              data-test-id="athlete-basic-info"
            >
              <span data-test-id="athlete-full-name">{{ userFullName }}</span>
              <span v-if="user.classYear && isMobile" data-test-id="class-year"> • {{ user.classYear }}</span>
              <span v-if="user.isTransfer && isMobile" data-test-id="transfer-tag"> Transfer</span>
            </h1>
            <div
              v-if="position"
              class="flex items-center justify-center ds-text-xs mt-1 md:hidden"
            >
              <h3 class="font-normal text-gray-700 m-0" data-test-id="position">
                {{ position }}
              </h3>
              <HoverTooltip
                v-if="remainingPositions > 0"
                class="text-blue-300 bg-gray-100 rounded-sm border-solid border-gray-100 border-b border-t border-l-4 border-r-4 ml-1"
              >
                <template #subject>
                  <span data-test-id="remaining-positions">+{{ remainingPositions }}</span>
                </template>
                <template #tooltip>
                  <div class="wrap" style="max-width: 160px" data-test-id="tooltip-position-list">
                    {{ remainingPositionsList }}
                  </div>
                </template>
              </HoverTooltip>
            </div>
            <h3
              v-if="position"
              class="font-medium text-gray-500 m-0 ds-text-sm mt-1 md:mt-0 hidden md:inline-block"
              data-test-id="md-position-list"
            >
              {{ positionList }}
            </h3>
          </div>
          <div v-if="isMobile" class="mt-3 flex gap-1 items-center">
            <InterestedPill v-if="athleteIsInterestedInProgram" />
            <SportPill
              v-if="isUserWithoutAccess && sportName"
              ref="sportPill"
              :sport="sportName"
              :gender="user.gender"
            />
            <CommittedPill />
          </div>
          <div
            class="absolute top-0 bottom-0 flex md:static md:ml-auto"
            :style="{
              paddingTop: isMobile ? '1rem' : '0',
              right: isMobile ? '0.75rem' : '0'
            }"
          >
            <ShareDropdown v-if="!isApplicableCollegeCoach && userFullName" :athlete-full-name="userFullName" />
            <CoachDropdown v-if="isApplicableCollegeCoach && userFullName" />
          </div>
        </div>
      </div>
    </Portal>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import Avatar from "../Avatar/index.vue"
import { mapGetters, mapState } from "vuex";
import { HoverTooltip } from "@sportsrecruits/playbook"
import CommittedPill from "./CommittedPill.vue";
import SportPill from "./SportPill.vue";
import ShareDropdown from "./ShareDropdown.vue";
import CoachDropdown from "./CoachDropdown.vue";
import DismissButton from "./CoachActions/DismissButton.vue";
import InterestedPill from "./InterestedPill.vue";

export default defineComponent({
  name: "AthleteIdentifyingBar",
  components: {
    InterestedPill,
    DismissButton,
    Avatar,
    CoachDropdown,
    CommittedPill,
    HoverTooltip,
    ShareDropdown,
    SportPill,
  },
  props: {
    dismissible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isScrolled: false,
    };
  },
  computed: {
    ...mapState('athleteProfile', ['user', 'isModal', 'showSecondaryNavbar']),
    ...mapGetters("athleteProfile", ["userFullName", "isApplicableCollegeCoach", "sportName", "isUserWithoutAccess", "athleteIsInterestedInProgram"]),
    avatarSize() {
      return this.isMobile ? 48 : 64;
    },
    topStickyAvatarSize() {
      return this.isMobile ? 32 : 48;
    },
    isMobile() {
      return ["xs", "sm", "mobile"].includes(this.$mq);
    },
    topStickyBar() {
      return this.isScrolled;
    },
    position() {
      return this.user.positions?.length > 0 ? this.user.positions.slice(0, 2).join(", ") : false;
    },
    positionList() {
      return this.user.positions.map(position => position).join(", ");
    },
    remainingPositions() {
      return this.user.positions?.length > 2 ? this.user.positions.length - 2 : 0;
    },
    remainingPositionsList() {
      return this.user.positions.slice(2).join(", ");
    },
  },
  mounted() {
    this.scrollParent = this.findScrollParent(this.$el);
    this.scrollParent.addEventListener('scroll', this.onScroll);
    this.onScroll()
    this.isLoaded = true;
  },
  beforeUnmount() {
    if (this.scrollParent) {
      this.scrollParent.removeEventListener('scroll', this.onScroll);
    }
  },
  methods: {
    findScrollParent(element) {
      let parent = element.parentNode;
      while (parent && parent !== document) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
        if (isScrollable) {
          return parent;
        }
        parent = parent.parentNode;
      }
      return window;
    },
    onScroll() {
      const athleteIdentifyingBar = document.getElementById('identifying-bar');
      if (!athleteIdentifyingBar) return;
      const athleteIdentifyingBarRect = athleteIdentifyingBar.getBoundingClientRect();

      if(athleteIdentifyingBarRect.height === 0) return;

      let topOffset = 0;

      if (this.isModal) {
        topOffset = 116
      }

      if (!this.showSecondaryNavbar) {
        topOffset = 58
      }

      const topStickyHeight = this.isMobile ? 48 : 64

      const scrollPos = this.scrollParent === window ? window.scrollY : this.scrollParent.scrollTop;
      this.isScrolled = scrollPos > athleteIdentifyingBarRect.height - topOffset + topStickyHeight;
    },
    close() {
      this.$emit("close");
    },
  },
})
</script>

<style scoped>
.fade-enter-active {
  transition: opacity .3s;
}

.fade-leave-active {
  transition: opacity .05s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
